// src/features/localization/KeyCreateDialog.tsx
// "+ New key" — desktop-app create dialog built on the WIP form-kit
// (FormDialog/TitleInput/AutoGrowTextarea) from src/components/form.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useLocalization } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import type { TranslationNamespace } from '@core/types/localization';
import { errMessage, makeEntity } from './helpers';

export interface KeyCreateDialogProps {
  open: boolean;
  namespace: TranslationNamespace | null;
  onClose: () => void;
  onCreated: () => void;
  onError: (message: string) => void;
}

export function KeyCreateDialog({ open, namespace, onClose, onCreated, onError }: KeyCreateDialogProps) {
  const { t } = useI18n();
  const scope = useScope();
  const store = useLocalization();

  const [key, setKey] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [placeholders, setPlaceholders] = useState('');
  const [isPlural, setIsPlural] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dirty = !!(key.trim() || sourceText.trim());

  const close = () => {
    setKey('');
    setSourceText('');
    setPlaceholders('');
    setIsPlural(false);
    onClose();
  };

  const submit = async () => {
    if (!key.trim() || !sourceText.trim() || !namespace?.entity?.id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await store.CreateTranslationKey({
        entity: makeEntity('', 'translation_key', scope),
        namespace: namespace.entity,
        key: key.trim(),
        source_text: sourceText.trim(),
        placeholders: placeholders.split(',').map((s) => s.trim()).filter(Boolean),
        is_plural: isPlural,
        tags: [],
      });
      close();
      onCreated();
    } catch (e) {
      onError(errMessage(e, t('localization.keys.saveError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('localization.keys.createSubmit')}
      canSubmit={!!key.trim() && !!sourceText.trim() && !!namespace}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('localization.keys.createTitle')}
      context={namespace ? <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">{namespace.name}</span> : undefined}
    >
      <div className="space-y-2 px-3.5 pb-4 pt-2">
        <TitleInput
          value={key}
          onChange={setKey}
          placeholder={t('localization.keys.keyPlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('key-create-source')?.focus()}
          aria-label={t('localization.keys.keyPlaceholder')}
          className="font-mono text-base"
        />
        <AutoGrowTextarea
          id="key-create-source"
          value={sourceText}
          onChange={setSourceText}
          placeholder={t('localization.keys.sourceTextPlaceholder')}
          minRows={2}
        />
        <input
          type="text"
          value={placeholders}
          onChange={(e) => setPlaceholders(e.target.value)}
          placeholder={t('localization.keys.placeholdersPlaceholder')}
          className="w-full rounded-md border border-dashed border-input bg-transparent px-2 py-1 text-sm text-foreground"
        />
        <ToggleSwitch label={t('localization.keys.isPlural')} checked={isPlural} onChange={setIsPlural} />
      </div>
    </FormDialog>
  );
}
